/**
 * PHASE 3 — location service.
 *
 * Responsibilities:
 *   - request geolocation permission and track live fixes,
 *   - remember the last known location across sessions (localStorage),
 *   - allow manual override (fallback when the user denies permission or the
 *     device has no GPS),
 *   - expose a small event channel so the app can react.
 *
 * Fully testable: the geolocation provider and storage are injected. On a
 * device, the caller passes `navigator.geolocation` and `localStorage`.
 */

import type {
  GeoLocation,
  GeoPositionErrorLike,
  GeoPositionLike,
  GeolocationProvider,
  LocationPermission,
} from "./types";

/** Used when the user has no location data at all: the prime meridian at the equator. */
export const DEFAULT_LOCATION: GeoLocation = {
  latDeg: 0,
  lonDeg: 0,
  altitudeM: null,
  accuracyM: null,
  source: "default",
  timestampMs: 0,
  isDeviceFix: false,
};

const STORAGE_KEY = "starlight-sky:lastLocation";

export class LocationService {
  private provider: GeolocationProvider | null;
  private storage: Pick<Storage, "getItem" | "setItem"> | null;
  private watchId: number | null = null;
  private current: GeoLocation = DEFAULT_LOCATION;
  private permission: LocationPermission;
  private listeners = new Set<(loc: GeoLocation) => void>();

  constructor(
    provider: GeolocationProvider | null = null,
    storage: Pick<Storage, "getItem" | "setItem"> | null = null,
  ) {
    this.provider = provider;
    this.storage = storage;
    this.permission = provider ? "undetermined" : "unavailable";
    this.current = this.restoreStored() ?? DEFAULT_LOCATION;
  }

  private restoreStored(): GeoLocation | null {
    try {
      const raw = this.storage?.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as GeoLocation;
      if (
        typeof parsed.latDeg === "number" &&
        typeof parsed.lonDeg === "number" &&
        Number.isFinite(parsed.latDeg) &&
        Number.isFinite(parsed.lonDeg) &&
        Math.abs(parsed.latDeg) <= 90 &&
        Math.abs(parsed.lonDeg) <= 180
      ) {
        return {
          latDeg: parsed.latDeg,
          lonDeg: parsed.lonDeg,
          altitudeM: typeof parsed.altitudeM === "number" ? parsed.altitudeM : null,
          accuracyM: typeof parsed.accuracyM === "number" ? parsed.accuracyM : null,
          source: "stored",
          timestampMs: typeof parsed.timestampMs === "number" ? parsed.timestampMs : 0,
          isDeviceFix: false,
        };
      }
    } catch {
      /* corrupted storage — fall through */
    }
    return null;
  }

  private persist(loc: GeoLocation) {
    try {
      this.storage?.setItem(
        STORAGE_KEY,
        JSON.stringify({ latDeg: loc.latDeg, lonDeg: loc.lonDeg, altitudeM: loc.altitudeM, timestampMs: loc.timestampMs }),
      );
    } catch {
      /* storage unavailable or full — non-fatal */
    }
  }

  private fromPosition(pos: GeoPositionLike): GeoLocation {
    return {
      latDeg: pos.coords.latitude,
      lonDeg: pos.coords.longitude,
      altitudeM: typeof pos.coords.altitude === "number" ? pos.coords.altitude : null,
      accuracyM: pos.coords.accuracy,
      source: "gps",
      timestampMs: pos.timestamp,
      isDeviceFix: true,
    };
  }

  private set(loc: GeoLocation) {
    this.current = loc;
    this.persist(loc);
    for (const listener of this.listeners) listener(loc);
  }

  /** Current best-known location. Never null — falls back to DEFAULT_LOCATION. */
  get(): GeoLocation {
    return this.current;
  }

  getPermission(): LocationPermission {
    return this.permission;
  }

  onChange(listener: (loc: GeoLocation) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Ask for a single fix. Resolves to the location or to a refusal result.
   */
  async requestFix(): Promise<GeoLocation> {
    const provider = this.provider;
    if (!provider) {
      this.permission = "unavailable";
      return this.current;
    }
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        resolve(this.current);
      }, 10_000);
      let settled = false;
      const done = (loc: GeoLocation) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        this.set(loc);
        resolve(loc);
      };
      provider.getCurrentPosition(
        (pos) => {
          this.permission = "granted";
          done(this.fromPosition(pos));
        },
        (err: GeoPositionErrorLike) => {
          if (err.code === 1) this.permission = "denied";
          done(this.current);
        },
        { enableHighAccuracy: true, timeout: 9000, maximumAge: 30_000 },
      );
    });
  }

  /** Start continuous tracking; stops any previous watch. */
  startTracking(): void {
    if (!this.provider || this.watchId !== null) return;
    this.watchId = this.provider.watchPosition(
      (pos) => {
        this.permission = "granted";
        this.set(this.fromPosition(pos));
      },
      (err: GeoPositionErrorLike) => {
        if (err.code === 1) this.permission = "denied";
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 5_000 },
    );
  }

  stopTracking(): void {
    if (this.watchId !== null && this.provider) {
      this.provider.clearWatch(this.watchId);
    }
    this.watchId = null;
  }

  /** Manual override (user picked a place on a map, or typed coordinates). */
  setManual(latDeg: number, lonDeg: number, altitudeM: number | null = null): GeoLocation {
    const loc: GeoLocation = {
      latDeg,
      lonDeg,
      altitudeM,
      accuracyM: null,
      source: "manual",
      timestampMs: Date.now(),
      isDeviceFix: false,
    };
    this.set(loc);
    return loc;
  }

  /** Forget manual/device data; used by tests and by a "reset" UI action. */
  clear() {
    try {
      this.storage?.setItem(STORAGE_KEY, "");
    } catch {
      /* ignore */
    }
    this.current = DEFAULT_LOCATION;
  }
}